<template>
  <Teleport v-if="hasMessages" to="body">
    <div
      ref="balloonRef"
      class="field-verify-error-overlay"
      :style="overlayStyle"
      role="alert"
    >
      <div v-for="(message, index) in normalized" :key="index" class="field-verify-error-overlay__msg">
        {{ message }}
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import {
  cssFixedFromVisualPoint,
  placeFieldVerifyErrorOverlay,
  type FieldVerifyErrorBox,
  type FieldVerifyErrorVisualScale,
} from '@/common/utils/placeFieldVerifyErrorOverlay'
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  messages?: string | string[] | null
  anchorEl?: HTMLElement | null
  /** Used when the passed node is not measurable yet. */
  fieldName?: string | null
}>()

const balloonRef = ref<HTMLElement | null>(null)
const placedOk = ref(false)
const box = ref<FieldVerifyErrorBox>({
  top: 0,
  left: 0,
  maxWidth: 360,
  placement: 'below',
})

const normalized = computed(() => {
  const raw = props.messages
  if (raw == null) return []
  const list = Array.isArray(raw) ? raw : [raw]
  return list.map((item) => String(item ?? '').trim()).filter((item) => item !== '')
})

const hasMessages = computed(() => normalized.value.length > 0)

const overlayStyle = computed(() => ({
  position: 'fixed',
  margin: '0',
  top: `${box.value.top}px`,
  left: `${box.value.left}px`,
  maxWidth: `${Math.max(160, box.value.maxWidth)}px`,
  visibility: placedOk.value ? 'visible' : 'hidden',
}))

const escapeCss = (value: string) =>
  typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(value) : value.replace(/"/g, '\\"')

const viewportIntersection = (rect: DOMRect): number => {
  if (typeof window === 'undefined') return 0
  const width = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0)
  const height = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
  if (width <= 1 || height <= 1) return 0
  return width * height
}

const measurableControl = (root: HTMLElement | null): HTMLElement | null => {
  if (!root) return null
  const candidates: HTMLElement[] = []
  if (root.matches('input, textarea, .p-inputtext')) candidates.push(root)
  root.querySelectorAll('input, textarea, .p-inputtext').forEach((node) => {
    if (node instanceof HTMLElement) candidates.push(node)
  })
  if (!candidates.length) candidates.push(root)
  let best: HTMLElement | null = null
  let bestArea = 0
  for (const candidate of candidates) {
    const area = viewportIntersection(candidate.getBoundingClientRect())
    if (area > bestArea) {
      best = candidate
      bestArea = area
    }
  }
  return best
}

const resolveAnchor = (): HTMLElement | null => {
  const fromProp = measurableControl(props.anchorEl ?? null)
  if (fromProp) return fromProp
  const fieldName = String(props.fieldName ?? '').trim()
  if (!fieldName || typeof document === 'undefined') return null
  const hosts = document.querySelectorAll(`[data-field-name="${escapeCss(fieldName)}"]`)
  let best: HTMLElement | null = null
  let bestArea = 0
  for (const host of hosts) {
    if (!(host instanceof HTMLElement)) continue
    const control = measurableControl(host)
    if (!control) continue
    const area = viewportIntersection(control.getBoundingClientRect())
    if (area > bestArea) {
      best = control
      bestArea = area
    }
  }
  return best
}

const measureFixedScale = (): FieldVerifyErrorVisualScale => {
  const identity: FieldVerifyErrorVisualScale = {
    originVisual: { top: 0, left: 0 },
    scaleX: 1,
    scaleY: 1,
  }
  if (typeof document === 'undefined') return identity
  const probe = document.createElement('div')
  probe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;margin:0;visibility:hidden;pointer-events:none;zoom:1;'
  document.body.appendChild(probe)
  const origin = probe.getBoundingClientRect()
  probe.style.top = '100px'
  probe.style.left = '100px'
  const shifted = probe.getBoundingClientRect()
  probe.remove()
  const scaleX = (shifted.left - origin.left) / 100
  const scaleY = (shifted.top - origin.top) / 100
  return {
    originVisual: { top: origin.top, left: origin.left },
    scaleX: Number.isFinite(scaleX) && scaleX !== 0 ? scaleX : 1,
    scaleY: Number.isFinite(scaleY) && scaleY !== 0 ? scaleY : 1,
  }
}

let frame = 0
let retries = 0
let anchorObserver: ResizeObserver | null = null
let cachedScale: FieldVerifyErrorVisualScale | null = null

const reposition = () => {
  if (!hasMessages.value) {
    placedOk.value = false
    retries = 0
    return
  }
  const anchor = resolveAnchor()
  const node = balloonRef.value
  if (!anchor || !node) {
    placedOk.value = false
    if (retries < 40) {
      retries += 1
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => reposition())
    }
    return
  }
  retries = 0
  const rect = anchor.getBoundingClientRect()
  const viewport = { width: window.innerWidth, height: window.innerHeight }
  const room = Math.max(160, viewport.width - Math.max(rect.left, 8) - 8)
  const desiredWidth = Math.min(360, Math.max(rect.width, Math.min(280, room)))
  node.style.maxWidth = `${desiredWidth}px`
  const anchorBox = {
    top: rect.top,
    left: rect.left,
    width: Math.max(rect.width, desiredWidth),
    height: rect.height,
    bottom: rect.bottom,
  }
  const placed = placeFieldVerifyErrorOverlay({
    anchor: anchorBox,
    balloonHeight: node.getBoundingClientRect().height || 28,
    viewport,
  })
  if (!cachedScale) cachedScale = measureFixedScale()
  const css = cssFixedFromVisualPoint(
    { top: placed.top, left: placed.left },
    cachedScale,
  )
  box.value = {
    top: css.top,
    left: css.left,
    maxWidth: Math.max(placed.maxWidth, 160),
    placement: placed.placement,
  }
  placedOk.value = true
}

watch(
  [hasMessages, () => props.anchorEl, () => props.fieldName, normalized],
  async ([visible]) => {
    if (!visible) {
      placedOk.value = false
      return
    }
    await nextTick()
    reposition()
  },
  { immediate: true, flush: 'post' },
)

watch(balloonRef, () => {
  retries = 0
  reposition()
})

const observeAnchor = (el: HTMLElement | null | undefined) => {
  anchorObserver?.disconnect()
  anchorObserver = null
  if (!el || typeof ResizeObserver === 'undefined') return
  anchorObserver = new ResizeObserver(() => reposition())
  anchorObserver.observe(el)
}

watch(() => props.anchorEl, (el) => observeAnchor(el), { immediate: true })

const onResize = () => {
  cachedScale = null
  reposition()
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  window.addEventListener('scroll', reposition, true)
  void nextTick().then(reposition)
})

onUnmounted(() => {
  cancelAnimationFrame(frame)
  anchorObserver?.disconnect()
  anchorObserver = null
  window.removeEventListener('resize', onResize)
  window.removeEventListener('scroll', reposition, true)
})
</script>

<style lang="scss">
/* On body, not #ui-popup-root. That portal zooms children again and the
   position sync walks a fixed box off the viewport. */
.field-verify-error-overlay {
  position: fixed;
  z-index: 13000;
  zoom: 1;
  box-sizing: border-box;
  width: max-content;
  min-width: 8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  font-family: inherit;
  font-size: 0.75rem;
  line-height: 1.35;
  white-space: normal;
  overflow-wrap: break-word;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  pointer-events: none;
}

.field-verify-error-overlay__msg + .field-verify-error-overlay__msg {
  margin-top: 0.125rem;
}
</style>
